import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { usePersonalization } from '@/contexts/PersonalizationContext';

const themeColorMaps = {
  cyberpunk: {
    '--primary': '270 80% 60%',
    '--accent': '320 90% 65%',
    '--ring': '270 80% 60%',
  },
  forest: {
    '--primary': '142 76% 36%',
    '--accent': '84 81% 44%',
    '--ring': '142 76% 36%',
  },
  sunset: {
    '--primary': '24 95% 53%',
    '--accent': '346 77% 49%',
    '--ring': '24 95% 53%',
  },
  ocean: {
    '--primary': '200 98% 39%',
    '--accent': '187 85% 53%',
    '--ring': '200 98% 39%',
  },
};

export function useThemeApplication() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { preferences } = usePersonalization();

  useEffect(() => {
    if (!preferences?.theme_preference) return;

    const themePreference = preferences.theme_preference;
    const currentTheme = theme === 'system' ? resolvedTheme : theme;
    
    // Only apply if different from current theme to prevent overriding user actions
    if (currentTheme === themePreference) return;

    // Handle base themes (system, light, dark)
    if (['system', 'light', 'dark'].includes(themePreference)) {
      setTheme(themePreference);
      // Reset to default colors
      const root = document.documentElement;
      Object.keys(themeColorMaps.cyberpunk).forEach(varName => {
        root.style.removeProperty(varName);
      });
    } 
    // Handle custom themed variants
    else if (themePreference in themeColorMaps) {
      // Set base theme to dark for custom themes
      setTheme('dark');
      
      // Apply custom colors
      const root = document.documentElement;
      const colors = themeColorMaps[themePreference as keyof typeof themeColorMaps];
      Object.entries(colors).forEach(([varName, value]) => {
        root.style.setProperty(varName, value);
      });
    }
  }, [preferences?.theme_preference, theme, resolvedTheme, setTheme]);
}
